import { useEffect, useMemo, useState } from 'react'
import { marketingApiService } from '../../services/marketingApiService'
import { loadItems } from '../email-composer/components/ContentPanel'
import { freeWeek, ownTimesOnly } from '../social/data/freeSlots'
import { loadReviews } from '../social/data/reviews'
import { loadPosts, subscribePosts } from '../social/data/store'
import { buildPlans, planForDay, seasonTemplates, planContext } from './data/planner'
import { plansFromPlaybook } from './data/playbook'
import { specialismFor } from '../email-composer/email/theme'
import { loadMemory, subscribeMemory, hydrateMemory } from './data/planStore'
import { hydrateJourneys } from '../../../marketing/journeys/journeysStore'
import { listCampaigns, subscribeCampaigns } from '../campaigns/data/campaignStore'
import { loadCampaigns as loadSends } from '../calendar/data/calendarData'
import { loadVouchers } from '../social/data/askData'
import { recallSoulText, rememberSoulText, loadSettings } from '../../../agent/frontdesk/api'
import { parseSoul } from '../../../agent/frontdesk/soul'
import moment from 'moment'
import agentService from '../../../../_services/agent.service'

// The salon's voice, from the soul it set on the front desk (null: not set)
const toneOf = (text) => {
  if (!text) return null
  try { return parseSoul(text).preset || null } catch (e) { return null }
}

// The soul as the server has it. It used to come only from this browser's
// cache, which Front Desk fills: on a device that never opened Front Desk
// (and on beta/prod, where Front Desk is hidden) plans had no tone at all.
// Falls back to the cache when the settings can't be read (e.g. a stylist
// without access to them).
const loadSoulText = () => loadSettings()
  .then((settings) => {
    const text = (settings && settings.greeting) || ''
    rememberSoulText(text)
    return text
  })
  .catch(() => recallSoulText())

// What the plans were last built from, this visit, per person and salon (in
// memory only: it holds reviews and the diary). Coming back to Marketing
// shows the same plans straight away while they're worked out again, instead
// of placeholders that the page then grows around.
const lastInputs = new Map()
// How long Bailey's plans wait for each source before going without it
export const SOURCE_TIMEOUT_MS = 8000
export const DIARY_TIMEOUT_MS = 15000
// p's value, or fallback if p fails or hasn't settled within ms
export const withTimeout = (p, fallback, ms) => {
  let timer
  return Promise.race([
    Promise.resolve(p).catch(() => fallback),
    new Promise((resolve) => { timer = setTimeout(() => resolve(fallback), ms) }),
  ]).finally(() => clearTimeout(timer))
}

const whoKey = () => { try { return `${localStorage.getItem('userId') || ''}:${localStorage.getItem('salonId') || ''}` } catch (e) { return ':' } }

// Bailey's plans for this salon, from the diary, reviews, team and audiences.
// { loading, plans, segments, reload }
export const usePlans = ({ dispatch, user, trade, salonName, brandColour, family, country = 'GB', links = {}, currency = '£', enabled = true }) => {
  const [inputs, setInputs] = useState(() => (enabled && lastInputs.get(whoKey())) || null)
  const [memory, setMemory] = useState(loadMemory)
  const [posts, setPosts] = useState(loadPosts)
  const [campaigns, setCampaigns] = useState(listCampaigns)
  const [sends, setSends] = useState([])
  const [nonce, setNonce] = useState(0)
  useEffect(() => subscribeMemory(() => setMemory(loadMemory())), [])
  // The server's copy (other devices, other staff at the salon); this
  // browser's cache shows straight away meanwhile
  useEffect(() => { if (enabled) hydrateMemory().catch(() => {}) }, [enabled])
  useEffect(() => subscribePosts(() => setPosts(loadPosts())), [])
  useEffect(() => subscribeCampaigns(() => setCampaigns(listCampaigns())), [])
  useEffect(() => {
    if (!enabled) return undefined
    let alive = true
    // Each source gets a time limit: one slow load (a big salon's month of
    // diary on weak 4G) must never hold Bailey's plans on the loading state.
    // A late source just means plans built without it.
    const safe = (p, fallback, ms = SOURCE_TIMEOUT_MS) => withTimeout(p, fallback, ms)
    Promise.all([
      // Four weeks of diary: quiet weeks and quiet days show up that far out
      safe(freeWeek({ days: 28 }), null, DIARY_TIMEOUT_MS),
      safe(loadReviews({ limit: 20 }).then(r => r.reviews), []),
      safe(loadItems('team', dispatch, user), []),
      safe(marketingApiService.getAudienceSegments(dispatch), []),
      safe(hydrateJourneys(localStorage.getItem('salonId')), null),
      safe(loadSends(dispatch), []),
      // Gift vouchers on sale online (null when they couldn't be read, so a
      // gifting season isn't turned into a booking one by mistake)
      safe(loadVouchers(), null),
      safe(loadSoulText(), null),
      // Their active Bailey goals: plans that move one come first (pathways)
      safe(agentService.goals().then(res => ((res && res.data && res.data.responseData) || [])
        .filter(g => g && g.status === 'active').map(g => ({ id: g._id, title: g.title, intent: g.intent }))), []),
      // Bailey's Playbook: the server's pick and two more for this person
      // (null where it's off, and then the plans are worked out here as before)
      safe(agentService.playbook({ surface: 'marketing' }).then(res => (res && res.data && res.data.responseData) || null), null),
    ]).then(([week, reviews, team, segments, journeys, sent, vouchers, soulText, goals, playbook]) => {
      if (!alive) return
      setSends(Array.isArray(sent) ? sent : [])
      const next = {
        week,
        journeys,
        reviews,
        team: (team || []).map(m => ({ id: m.id, name: m.name, role: m.title && m.title !== 'Stylist' ? m.title : '', image: m.image })),
        segments: Array.isArray(segments) ? segments : [],
        vouchers: Array.isArray(vouchers) ? vouchers : null,
        tone: toneOf(soulText),
        goals: Array.isArray(goals) ? goals : [],
        playbook: playbook && playbook.pick ? playbook : null,
      }
      lastInputs.set(whoKey(), next)
      setInputs(next)
    })
    return () => { alive = false }
  }, [enabled, nonce]) // eslint-disable-line react-hooks/exhaustive-deps
  const linksKey = JSON.stringify(links || {})
  // A self-employed stylist's plans are for their own book (planner.js)
  const self = ownTimesOnly() ? { id: localStorage.getItem('userId') } : null
  const me = useMemo(() => {
    const info = (user && user.userInfo) || {}
    return { name: info.name || [info.first_name, info.last_name].filter(Boolean).join(' ') || '' }
  }, [user])
  // A beauty business's specialism (nails, lashes, skin) for the words
  const specialism = useMemo(() => {
    const hd = (user && user.userInfo && user.userInfo.hairdresser) || {}
    return specialismFor((hd.salon && hd.salon.businessType) || null)
  }, [user])
  const plans = useMemo(() => {
    if (!inputs) return []
    const args = { ...inputs, trade, salonName, brandColour, family, posts, memory, country, campaigns, links, currency, self, me, specialism }
    // The server chose: its pick and alternatives, built by the same recipes
    if (inputs.playbook) {
      const fromPlaybook = plansFromPlaybook(inputs.playbook, { ...planContext(args), noTexts: !!inputs.playbook.noTexts })
      if (fromPlaybook.length) return fromPlaybook
    }
    return buildPlans(args)
  }, [inputs, trade, salonName, brandColour, family, posts, memory, country, campaigns, linksKey, currency, me, specialism]) // eslint-disable-line react-hooks/exhaustive-deps
  // Every season in the next year, for the start page
  const templates = useMemo(() => seasonTemplates({ ...(inputs || {}), trade, salonName, brandColour, family, country, posts, links, currency, segments: (inputs && inputs.segments) || [], week: (inputs && inputs.week) || [] }),
    [trade, salonName, brandColour, family, country, inputs, posts, linksKey, currency]) // eslint-disable-line react-hooks/exhaustive-deps
  // What else is going out, for the builder's frequency rules: scheduled
  // emails and texts on the server, and approved campaigns on this device
  const others = useMemo(() => {
    const now = moment()
    const out = sends.filter(c => c.status === 'scheduled' && c.at && moment(c.at).isAfter(now)).map(c => ({ channel: c.kind, at: c.at, name: c.title, apiId: String(c.id) }))
    return out
  }, [sends])
  const forDay = (day) => planForDay({ day, trade, salonName, brandColour, family, week: (inputs && inputs.week) || [], segments: (inputs && inputs.segments) || [], posts })
  // What a campaign built by hand can draw on: the same real things
  const things = useMemo(() => ({ ...(inputs || {}), posts, links, currency }), [inputs, posts, linksKey, currency]) // eslint-disable-line react-hooks/exhaustive-deps
  return { loading: !inputs, plans, playbook: (inputs && inputs.playbook) || null, templates, others, campaigns, things, segments: (inputs && inputs.segments) || [], week: (inputs && inputs.week) || [], planForDay: forDay, reload: () => setNonce(n => n + 1) }
}
